import {
  DateFormat,
  formatDate,
  isValidDate,
  parseDate,
} from '../../../cdk/date';
import { toPartialMatchRegex } from '../../../cdk/utils';
import type { InputAdapter } from './input.adapter';
import { isEditing } from './primitives';

enum DatePart {
  year = 'yyyy',
  month = 'MM',
  day = 'dd',
  hour = 'HH',
  minute = 'mm',
  second = 'ss',
  millisecond = 'SSS',
  shortMonth = 'MMM',
  longMonth = 'MMMM',
}

export class DateAdapter implements InputAdapter<Date> {
  readonly #re: RegExp;
  readonly #separators: string[] = [];

  constructor(readonly dateFormat: DateFormat = DateFormat.DMY) {
    let pattern = '^';
    const parts = dateFormat.split(/[^\w]/);
    this.#separators = dateFormat.split(/\w/).filter(Boolean);
    parts.forEach((part, i) => {
      switch (part) {
        case DatePart.shortMonth:
          pattern += '\\w{3}';
          break;
        case DatePart.longMonth:
          pattern += '\\w+';
          break;
        default:
          pattern += `\\d`.repeat(part.length);
      }
      pattern += this.#separators[i] ? `[${this.#separators[i]}]` : '';
    });
    pattern += '$';
    this.#re = toPartialMatchRegex(new RegExp(pattern));
  }

  format(value: Date | string | null | undefined): string {
    if (!value) {
      return '';
    }

    if (value instanceof Date) {
      return formatDate(value, {
        format: this.dateFormat,
        useNativeLiterals: false,
        useUTC: true,
      });
    }

    const maskPattern = this.dateFormat.replace(/[a-zA-Z]/g, '9'); // Replace letters with '9' (numeric placeholders)

    value = value.replace(/\D/g, ''); // Remove all non-numeric characters
    let formattedValue = '';
    let valueIndex = 0;

    for (let i = 0; i < maskPattern.length; i++) {
      if (maskPattern[i] === '9') {
        if (value[valueIndex]) {
          formattedValue += value[valueIndex];
          valueIndex++;
        } else {
          break;
        }
      } else {
        formattedValue += maskPattern[i];
      }
    }

    return formattedValue;
  }

  parse(value: string | null | Date): Date | null {
    if (value === '' || value === undefined || value === null) return null;
    if (value instanceof Date && isValidDate(value)) return value;
    return parseDate(value, this.dateFormat);
  }

  keydownHandler(e: KeyboardEvent): void {
    const el = e.target as HTMLInputElement;
    if (e.key === 'Backspace') {
      if (el.selectionStart! > 0) {
        const pos = el.selectionStart!;
        if (this.#separators.includes(el.value[pos - 1])) {
          e.preventDefault();
          el.value = el.value.slice(0, pos - 2) + el.value.slice(pos - 1);
          el.setSelectionRange(pos - 2, pos - 2);
        }
      }
    }
    if (isEditing(e)) {
      return;
    }
    const index = el.selectionStart || 0;
    const val = el.value;
    const newVal = val.slice(0, index).concat(e.key, val.slice(index + 1));
    if (!this.#re.test(newVal)) {
      e.preventDefault();
      return;
    }

    // trim special chars from start and end
    const trimmedVal = newVal.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '');
    const partialFormat = this.dateFormat.slice(0, trimmedVal.length);

    if (!parseDate(trimmedVal, partialFormat as DateFormat)) {
      e.preventDefault();
      return;
    }
  }
}
